import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ButtonIcon, Menu, MenuItem, MenuTrigger } from '@backstage/ui';
import { useSidebarOpenState } from '@backstage/core-components';
import type { IconComponent } from '@backstage/core-plugin-api';
import { Icon } from '@internal/ui';
import styles from './SidebarNavGroup.module.css';

export interface SidebarNavGroupItem {
  id: string;
  label: string;
  /** Absolute path; the item is current on it and anything below it. */
  href: string;
  /** A Material Icons name, as on the plugin's header tabs, or any node such as an Avatar. */
  icon?: string | ReactNode;
}

export interface SidebarNavGroupProps {
  icon: IconComponent;
  text: string;
  /** Plugin tab lists (`{ id, label, href }`) fit as-is. */
  items: readonly SidebarNavGroupItem[];
  /** Makes the header a link to the section; the chevron alone then toggles. */
  to?: string;
}

const isCurrent = (pathname: string, href: string) => {
  const path = pathname.toLowerCase();
  const target = href.toLowerCase();
  return path === target || path.startsWith(`${target}/`);
};

const itemIcon = (icon: SidebarNavGroupItem['icon']) =>
  typeof icon === 'string' ? <Icon name={icon} size={14} /> : icon;

/**
 * DS SidebarNav group: an inline, collapsible sub-list in the open sidebar and
 * a menu off the rail. It opens itself whenever the current page is one of its items.
 */
export function SidebarNavGroup({
  icon: GroupIcon,
  text,
  items,
  to,
}: SidebarNavGroupProps) {
  const { isOpen: sidebarOpen } = useSidebarOpenState();
  const { pathname } = useLocation();
  const current = items.find(item => isCurrent(pathname, item.href));
  const [open, setOpen] = useState(!!current);
  const listId = useId();

  const currentId = current?.id;
  useEffect(() => {
    if (currentId) setOpen(true);
  }, [currentId]);

  if (!sidebarOpen) {
    return (
      <MenuTrigger>
        <ButtonIcon
          variant="tertiary"
          size="medium"
          aria-label={text}
          icon={<GroupIcon fontSize="small" />}
          className={styles.rail}
          data-current={current ? '' : undefined}
        />
        <Menu placement="right top">
          {items.map(item => (
            <MenuItem
              key={item.id}
              id={item.id}
              href={item.href}
              iconStart={itemIcon(item.icon)}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </MenuTrigger>
    );
  }

  return (
    <>
      {to ? (
        <div className={styles.trigger}>
          <Link to={to} className={styles.headerLink}>
            <span className={styles.icon}>
              <GroupIcon fontSize="small" />
            </span>
            <span className={styles.label}>{text}</span>
          </Link>
          <button
            type="button"
            className={styles.chevronButton}
            aria-label={`${text} submenu`}
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            onClick={() => setOpen(!open)}
          >
            <Icon name="expand_less" size={14} className={styles.chevron} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.trigger}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={() => setOpen(!open)}
        >
          <span className={styles.icon}>
            <GroupIcon fontSize="small" />
          </span>
          <span className={styles.label}>{text}</span>
          <Icon name="expand_less" size={14} className={styles.chevron} />
        </button>
      )}
      {open && (
        <div id={listId} role="group" aria-label={text} className={styles.list}>
          {items.map(item => (
            <Link
              key={item.id}
              to={item.href}
              className={styles.item}
              aria-current={item === current ? 'page' : undefined}
            >
              {itemIcon(item.icon)}
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
